// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getFileExtension from "@insite/client-framework/Common/Utilities/getFileExtension";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getQuoteState } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import FileUpload from "@insite/mobius/FileUpload";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Link from "@insite/mobius/Link";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CloseCircleFill from "@store/Library/Components/Toolkit/Icons/CloseCircle/CloseCircleFill";
import OrderActionSet, {
    OrderActionSetPresentationProps,
} from "@store/Library/Components/Toolkit/Order/OrderActionSet";
import { boxStyles } from "@store/Themes/common/mixins/box";
import { linkStyles } from "@store/Themes/common/mixins/link";
import getQuoteDetailsDocument from "@store/Framework/Store/Store/Pages/RfqQuoteDetails/Handlers/GetQuoteDetailsDocument";
import getQuoteDetailsDocuments from "@store/Framework/Store/Store/Pages/RfqQuoteDetails/Handlers/GetQuoteDetailsDocuments";
import removeQuoteDetailsDocument from "@store/Framework/Store/Store/Pages/RfqQuoteDetails/Handlers/RemoveQuoteDetailsDocument";
import setQuoteDetailsDocuments from "@store/Framework/Store/Store/Pages/RfqQuoteDetails/Handlers/SetQuoteDetailsDocuments";
import {
    useRfqDetailsDocuments,
    useRfqDetailsUploadMsg,
} from "@store/Framework/Store/Store/Pages/RfqQuoteDetails/StoreRfqQuoteDetailsHooks";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { spacingStyles } from "@toolkit/Themes/common/mixins/spacing";
import React, { useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    quoteState: getQuoteState(state, state.pages.rfqQuoteDetails.quoteId),
});

const mapDispatchToProps = {
    getQuoteDetailsDocument,
    getQuoteDetailsDocuments,
    setQuoteDetailsDocuments,
    removeQuoteDetailsDocument,
};

type Props = WidgetProps &
    HasHistory &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext;

export interface RfqQuoteDetailsDocumentsStyles {
    mainWrapper?: InjectableCss;
    actionSet?: OrderActionSetPresentationProps;
    titleText?: TypographyPresentationProps;
    addPdfButton?: ButtonPresentationProps;
    uploadPdfButton?: ButtonPresentationProps;
    uploadPdfClickable?: LinkPresentationProps;
    docNameWrapper?: InjectableCss;
    docLink?: LinkPresentationProps;
    removeLink?: LinkPresentationProps;
}

export const rfqQuoteDetailsDocumentsStyles: RfqQuoteDetailsDocumentsStyles = {
    mainWrapper: {
        // Sync with RfqQuoteDetailsMessages.tsx
        css: css`
            display: flex;
            flex-direction: column;
            ${boxStyles([pxToRem(15), null, pxToRem(25), null, null])}
            ${spacingStyles(pxToRem(24), pxToRem(24))}
            background-color: ${getColor("common.accent")};
        `,
    },
    titleText: {
        variant: "h4",
        forwardAs: "h2",
    },
    docNameWrapper: {
        css: css`
            display: flex;
            width: 100%;
            justify-content: space-between;
            margin-bottom: ${pxToRem(16)};
        `,
    },
    docLink: {
        css: css`
            ${linkStyles("inherit", "header.hover", false, true)}
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            font-size: ${x => x.theme.design.typography.size.smaller};
            line-height: ${x => x.theme.design.typography.lineHeight.small};
        `,
    },
    removeLink: {
        icon: { iconProps: { src: CloseCircleFill, color: "primary.dark", size: 20 } },
        css: css`
            ${linkStyles("inherit", "header.hover", false, true)}
            font-size: ${x => x.theme.design.typography.size.smaller};
            line-height: ${x => x.theme.design.typography.lineHeight.small};
        `,
    },
};

const styles = rfqQuoteDetailsDocumentsStyles;

const RfqQuoteDetailsDocuments = ({
    quoteState,
    getQuoteDetailsDocument,
    setQuoteDetailsDocuments,
    removeQuoteDetailsDocument,
    toaster,
}: Props) => {
    const rfqDetailsDocuments = useRfqDetailsDocuments();
    const docUploadMsg = useRfqDetailsUploadMsg();
    const fileUploadRef = useRef({ value: "" } as HTMLInputElement);
    const [file, setFile] = useState<any>(null);
    const [incorrectFileExtension, setIncorrectFileExtension] = useState(false);
    const [fileUploaded, setFileUploaded] = useState(false);

    useEffect(() => {
        if (fileUploaded) {
            toaster.addToast({
                body: docUploadMsg,
                messageType: "success",
                timeoutLength: 6000,
            });
        }
        setFileUploaded(false);
    }, [fileUploaded]);

    if (!quoteState.value) {
        return null;
    }

    const quote = quoteState.value;

    const fileChangeHandler = (event: React.ChangeEvent<any>) => {
        const uploadedFile = event.target.files[0];
        setFile(uploadedFile);
    };

    const uploadPdfClickHandler = () => {
        const reader = new FileReader();
        reader.onload = onReaderLoad(file.name, getFileExtension(file.name));
        reader.readAsArrayBuffer(file);
    };

    const onReaderLoad = (fileName: string, fileExtension: string) => {
        return (e: Event) => {
            const data = (e.target as any).result;
            const base64String = btoa(
                new Uint8Array(data).reduce((data, byte) => data + String.fromCharCode(byte), ""),
            );

            if (fileExtension === "pdf") {
                setQuoteDetailsDocuments({
                    fileStream: base64String,
                    fileName,
                    orderNumber: quote.orderNumber,
                    onSuccess: () => {
                        setFileUploaded(true);

                        getQuoteDetailsDocuments({ orderNumber: quote.orderNumber });
                    },
                });
            } else {
                setIncorrectFileExtension(true);
            }
        };
    };

    const documentClickHandler = (document: string) => {
        getQuoteDetailsDocument({ orderNumber: quote.orderNumber, documentName: document });
    };

    const fileRemoveDocumentHandler = (document: string) => {
        removeQuoteDetailsDocument({
            orderNumber: quote.orderNumber,
            documentName: document,
            onSuccess: () => {
                toaster.addToast({
                    body: `The file ${document} was deleted successfully`,
                    messageType: "success",
                    timeoutLength: 6000,
                });
                getQuoteDetailsDocuments({ orderNumber: quote.orderNumber });
            },
        });
    };

    const uploadPdfLabel = translate("Upload PDF");
    const titleText = translate("Documents");

    return (rfqDetailsDocuments?.length && rfqDetailsDocuments?.length > 0) || quote.isSalesperson ? (
        <StyledWrapper {...styles.mainWrapper}>
            {rfqDetailsDocuments?.length && rfqDetailsDocuments?.length > 0 ? (
                <StyledWrapper>
                    <Typography as="p" {...styles.titleText}>
                        {titleText}
                    </Typography>
                    {rfqDetailsDocuments?.map((document, index) => (
                        <StyledWrapper key={index} {...styles.docNameWrapper}>
                            <Link {...styles.docLink} key={index} onClick={() => documentClickHandler(document)}>
                                {document}
                            </Link>
                            {quote.isSalesperson && (
                                <Link {...styles.removeLink} onClick={() => fileRemoveDocumentHandler(document)} />
                            )}
                        </StyledWrapper>
                    ))}
                </StyledWrapper>
            ) : null}
            {quote.isSalesperson && (
                <>
                    <FileUpload
                        inputRef={fileUploadRef}
                        fileName={file?.name || ""}
                        accept=".pdf"
                        label={translate("Add Pdf")}
                        labelPosition="top"
                        error={incorrectFileExtension ? translate("File must be pdf") : undefined}
                        onFileChange={fileChangeHandler}
                        uid="rfqQuoteDetailsFileUpload"
                    />
                    <OrderActionSet
                        {...styles.actionSet}
                        actions={[
                            {
                                key: "uploadPdf",
                                label: uploadPdfLabel,
                                onClick: () => uploadPdfClickHandler(),
                                buttonStyles: styles.uploadPdfButton,
                                linkStyles: styles.uploadPdfClickable,
                            },
                        ]}
                    />
                </>
            )}
        </StyledWrapper>
    ) : null;
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(RfqQuoteDetailsDocuments))),
    definition: {
        group: "RFQ Quote Details",
        allowedContexts: ["RfqQuoteDetailsPage"],
        displayName: "Documents",
    },
};

export default widgetModule;
